import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
} from 'react';
import type { DirEntry, PlatformBridge, ToolHost } from '@slate/core';
import {
  ChevronDown,
  ChevronRight,
  Clipboard,
  Copy,
  FilePlus,
  FileText,
  Folder,
  FolderPlus,
  FolderTree,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useDocStore } from '../store/docStore';
import { openPath } from '../store/docActions';
import { useContextMenu, useLongPress, type ContextMenuEntry } from '../context-menu';
import { useToolContributionsFor } from '../tools/useToolContributionsFor';
import { resolveContributionIcon } from '../tools/contributionIcon';
import { isReadableFile } from '../util/fileType';
import { FileTypeIcon } from '../icons/FileTypeIcon';
import { keyLabel, modPressed, shortcutLabel } from '../keymap/platform';
import {
  createFileIn,
  createFolderIn,
  duplicateNode,
  pasteSources,
  pickDuplicateName,
  pickUntitledName,
  removeNodes,
  renameNode,
  trashNodes,
  undoAffectedParents,
  undoLast,
  type FsActionsContext,
  type PasteConflictChoice,
  type PasteConflictPrompt,
  type PasteConflictResolver,
} from '../fs/fsActions';
import { basename, isUnderRoot, joinPath } from '../fs/containment';
import { useTreeSpeedSearch } from './useTreeSpeedSearch';
import { toast } from '../store/toastStore';
import { InlineEditInput } from './InlineEditInput';
import { Modal, type ModalAction } from './Modal';
import { PasteConflictDialog } from './PasteConflictDialog';
import {
  EMPTY_SELECTION,
  pruneSelection,
  selectOnly,
  selectRange,
  toggleOne,
  type SelectionState,
} from '../fs/selection';
import { useFsClipboardStore } from '../store/fsClipboardStore';
import { useFsDragStore } from '../store/fsDragStore';
import { useFsUndoStore } from '../store/fsUndoStore';
import { ClipboardCopy, Scissors, ClipboardPaste, Undo2 } from 'lucide-react';

export interface FileTreeProps {
  platform: PlatformBridge;
  /** Absolute root directory path to render children of. */
  rootPath: string;
  /**
   * Accessible name for this tree. With multiple roots each tree must have a
   * distinct name (e.g. "Files in <root>") so assistive tech can tell them apart.
   */
  label?: string;
  /**
   * Host handed to tool-contribution menu actions (e.g. the terminal's
   * "Open Terminal Here"). When absent, contribution items are omitted — the
   * slice still renders, just without the tool entries.
   */
  toolHost?: ToolHost;
}

/**
 * Imperative handle on a FileTree, used by the sidebar's root-group header so
 * the root row's "+" button and context menu can trigger an inline-create
 * inside the tree even though the root header itself lives outside the
 * tree's DOM.
 */
export interface FileTreeHandle {
  /** Start an inline-create at the tree root. The new row appears at the top
   *  of the listing with a sensible placeholder name. */
  startCreateInRoot(kind: 'file' | 'folder'): void;
  /**
   * Paste the current FS-clipboard contents into THIS tree's workspace
   * root. Used by the sidebar's root-header context-menu "Paste" entry.
   * Mirrors the tree row's Paste action: dedupes with the conflict
   * dialog, journals the inverse for undo, re-anchors moved open tabs.
   * No-ops when the clipboard is empty.
   */
  pasteIntoRoot(): Promise<void>;
  /**
   * Paste tree-row sources into THIS tree's workspace root. Used by the
   * sidebar's group header drop handler so dropping on the header (which
   * sits outside the tree's own DOM) still lands at the root. Returns
   * after the FS ops + journal + refresh complete.
   */
  dropAtRoot(sources: string[], alt: boolean): Promise<void>;
  /**
   * Forward a drag-hover tick from outside the FileTree's own DOM (e.g.
   * the Sidebar group header) so the same visual feedback — badge,
   * sidebar accent, dragOverPath — fires consistently.
   */
  reportHover(clientX: number, clientY: number, alt: boolean): void;
  /** Clear the hover-only visual state. Source tracking is preserved. */
  reportHoverEnd(): void;
}

/** A node as laid out in the visible (flattened) tree order. */
interface VisibleNode {
  entry: DirEntry;
  /** 1-based depth (aria-level); root entries are level 1. */
  level: number;
  /** Path of the parent directory, or null for root entries. */
  parentPath: string | null;
}

/**
 * State while the user is renaming an existing row. The row's label is
 * replaced with an `<input>`; the original `path` stays so we can target
 * the FS op.
 */
interface RenameState {
  path: string;
  value: string;
}

/**
 * State while the user is inline-creating a new file or folder under a
 * parent directory. The synthetic row sits directly under the parent in the
 * visible list. Commit fires `fs.createFile` / `fs.mkdir`.
 */
interface CreateState {
  parentPath: string;
  kind: 'file' | 'folder';
  value: string;
}

/**
 * Lazy, collapsible workspace file tree implementing the WAI-ARIA tree pattern.
 *
 * Tree state (expanded folders, lazily-loaded children, focus) is owned here so
 * keyboard navigation can walk a single flattened list of *visible* nodes.
 * Folders expand on demand via `platform.readDir`; clicking/activating a file
 * reads it and opens the doc. A roving tabindex keeps exactly one row in the tab
 * order at a time; the active (open) file is highlighted + `aria-selected`.
 *
 * Phase 2a (sidebar file-manager) layers on per-row mutation affordances:
 *   - `+` button per folder row opens an inline-create input (file or folder).
 *   - F2 (or the context-menu "Rename" entry) starts an inline rename of
 *     the focused row.
 *   - Right-click / long-press context menu adds Rename / Duplicate /
 *     Move to Trash, plus New File / New Folder on folders.
 *   - Delete / Backspace key on a focused row moves it to the OS trash.
 *
 * After every successful FS op the affected folder's children are re-fetched
 * via `platform.readDir` so the tree reflects the new state without a manual
 * reload.
 */
export const FileTree = forwardRef<FileTreeHandle, FileTreeProps>(function FileTree(
  { platform, rootPath, label = 'Workspace files', toolHost },
  ref,
): ReactElement {
  const activeId = useDocStore((s) => s.activeId);
  // Tool-contributed directory menu items (e.g. terminal's "Open Terminal
  // Here"). Subscribed so a tool registering later refreshes the menu builder.
  const dirContributions = useToolContributionsFor('directoryContextMenu');
  const workspaceRoots = useDocStore((s) => s.workspaceRoots);
  const addWorkspaceRoot = useDocStore((s) => s.addWorkspaceRoot);
  const { openContextMenu } = useContextMenu();

  const [roots, setRoots] = useState<DirEntry[] | null>(null);
  const [error, setError] = useState(false);
  // Per-folder lazily-loaded children (null = not yet loaded).
  const [children, setChildren] = useState<Map<string, DirEntry[]>>(new Map());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<Set<string>>(new Set());
  // The roving-tabindex focused node (by path). Null until first interaction.
  const [focusedPath, setFocusedPath] = useState<string | null>(null);
  // Inline-rename / inline-create state (mutually exclusive — at most one active).
  const [rename, setRename] = useState<RenameState | null>(null);
  const [create, setCreate] = useState<CreateState | null>(null);
  // Pending trash confirmation. The actual fs.trash call doesn't fire until
  // the user confirms in the modal — destructive ops always require an
  // explicit "yes".
  const [trashConfirm, setTrashConfirm] = useState<
    { paths: string[]; parentPaths: string[] } | null
  >(null);
  /** Pending Shift-Delete confirmation. Irreversible — stronger wording. */
  const [permanentConfirm, setPermanentConfirm] = useState<
    { paths: string[]; parentPaths: string[] } | null
  >(null);
  /**
   * Multi-select state machine (path-set + range anchor). Click replaces;
   * Cmd/Ctrl-click toggles; Shift-click ranges from the anchor through the
   * clicked row.
   */
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  /** Active conflict prompt + its pending resolver, set by `pasteSources`. */
  const [conflict, setConflict] = useState<
    | {
        prompt: PasteConflictPrompt;
        resolve: (choice: PasteConflictChoice) => void;
      }
    | null
  >(null);
  /** Path of the folder row currently being hovered as a drop target. */
  const [dragOverPath, setDragOverPath] = useState<string | null>(null);
  /**
   * In-flight drag sources, sourced from the SHARED `useFsDragStore` so
   * cross-root drops work: when the user drags a row from tree A onto
   * a row in tree B, B reads A's source list through this same store
   * (a local-per-tree ref couldn't satisfy that). Drives:
   *   1. The source rows' dim (`data-drag-source`).
   *   2. The self/descendant drop guard.
   *   3. The drop runner's source list when the bridge supplies empty paths.
   * Writes on `dragstart` / clears on the bridge's `onDrop` / `onLeave`.
   */
  const dragSources = useFsDragStore((s) => s.sources);
  /**
   * Cursor-following badge shown during an OS-drag-in over THIS tree's rows.
   * `mode` reflects the live modifier state (Alt = move; else copy); `x`/`y`
   * are in window CSS px and drive an absolutely-positioned chip.
   */
  const [dragBadge, setDragBadge] = useState<
    { mode: 'copy' | 'move'; x: number; y: number } | null
  >(null);
  /** Subscribed clipboard state — drives the "cut" dimming on rows. */
  const clipboardMode = useFsClipboardStore((s) => s.mode);
  const clipboardPaths = useFsClipboardStore((s) => s.paths);
  const clipboardSet = useMemo(
    () => new Set(clipboardMode === 'cut' ? clipboardPaths : []),
    [clipboardMode, clipboardPaths],
  );

  // DOM refs per row, so keyboard navigation can move focus imperatively.
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // When set, focus this path's row after the next render (e.g. after expand).
  const pendingFocus = useRef<string | null>(null);
  // After a successful FS op, focus this path once it materialises in the tree.
  const pendingFsFocus = useRef<string | null>(null);

  /**
   * `FsActionsContext` reused by every fsAction call. Toast is the shared
   * store-level helper so call sites don't need to subscribe.
   */
  const fsCtx = useMemo<FsActionsContext>(
    () => ({
      platform,
      workspaceRoot: rootPath,
      toast: (message, opts) => {
        toast(message, opts);
      },
    }),
    [platform, rootPath],
  );

  // --- root listing ---
  useEffect(() => {
    let alive = true;
    setRoots(null);
    setError(false);
    setChildren(new Map());
    setExpanded(new Set());
    setLoading(new Set());
    setFocusedPath(null);
    setRename(null);
    setCreate(null);
    platform
      .readDir(rootPath)
      .then((list) => {
        if (alive) setRoots(list);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [platform, rootPath]);

  // Lazily load a folder's children (no-op if already loaded/loading).
  const loadChildren = useCallback(
    (path: string): void => {
      if (children.has(path) || loading.has(path)) return;
      setLoading((s) => new Set(s).add(path));
      platform
        .readDir(path)
        .then((list) => {
          setChildren((m) => new Map(m).set(path, list));
        })
        .catch(() => {
          setChildren((m) => new Map(m).set(path, []));
        })
        .finally(() => {
          setLoading((s) => {
            const next = new Set(s);
            next.delete(path);
            return next;
          });
        });
    },
    [platform, children, loading],
  );

  /**
   * Re-fetch a folder's children after an FS op. Unlike `loadChildren`, this
   * runs even when the folder is already in the cache — it's the post-op
   * refresh path.
   */
  const refreshDir = useCallback(
    async (path: string): Promise<void> => {
      // `null` means refresh the root listing.
      if (path === rootPath || path === '') {
        try {
          const list = await platform.readDir(rootPath);
          setRoots(list);
        } catch {
          /* swallow — leaves the previous listing in place */
        }
        return;
      }
      try {
        const list = await platform.readDir(path);
        setChildren((m) => new Map(m).set(path, list));
      } catch {
        /* swallow — keep the stale cache rather than blanking the row */
      }
    },
    [platform, rootPath],
  );

  // ─────────── recursive workspace watch (external + internal changes) ───────────
  // Subscribe to a recursive watcher on the workspace root. When the watcher
  // fires (file added, removed, renamed, modified anywhere under the root —
  // by us, by an external editor, or by another tool), figure out which
  // directory's listing actually changed and re-readDir just that one. We
  // refresh the affected node's PARENT (its listing is what shifted), unless
  // the affected path isn't currently in our cache, in which case we walk
  // upward to find the nearest ancestor we *do* have cached and refresh
  // that. Debouncing already happens in the Rust watcher; here we just
  // route.
  useEffect(() => {
    if (typeof platform.watchDir !== 'function') return;
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    platform
      .watchDir(rootPath, (affectedPath) => {
        if (cancelled) return;
        // Affected node lives under the root by construction. Find the
        // nearest cached parent (rootPath OR an entry in `children`) and
        // refresh it.
        const parent = affectedPath.replace(/[\\/][^\\/]*$/, '');
        const isUnderRoot = parent === rootPath || parent.startsWith(`${rootPath}/`);
        if (!isUnderRoot) return;
        // If we have a cache for that exact parent (root listing or a
        // children entry), refresh it. Otherwise walk up until we find one;
        // any ancestor whose listing now omits the affected branch will
        // self-prune via React state.
        let target = parent;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (target === rootPath || children.has(target)) {
            void refreshDir(target);
            break;
          }
          if (!target.startsWith(`${rootPath}/`)) break; // safety
          const next = target.replace(/[\\/][^\\/]*$/, '');
          if (next === target) break;
          target = next;
        }
      })
      .then((unsub) => {
        if (cancelled) {
          unsub();
        } else {
          unsubscribe = unsub;
        }
      })
      .catch(() => {
        /* watchDir unsupported on this platform — fall back to manual refresh */
      });
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
    // Re-subscribe when the root changes (a workspace folder swap). `children`
    // is intentionally OUT of the deps — re-subscribing on every cache change
    // would spam the watcher; the handler reads `children` via closure and
    // tolerates stale snapshots (the worst case is one extra refreshDir for
    // an ancestor we just learned about).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, rootPath, refreshDir]);

  // Drag-hover visual machinery + the two subscriptions (OS drag-in via
  // platform.onDropFiles, and document-level dragover for internal HTML5
  // drags) live further down — they need `isDropDisabled`, `resolveDragMode`,
  // `treeRootRef`, and the snapshot refs to all exist, so they're placed
  // after those declarations.

  const expand = useCallback(
    (path: string): void => {
      setExpanded((s) => new Set(s).add(path));
      loadChildren(path);
    },
    [loadChildren],
  );

  const collapse = useCallback((path: string): void => {
    setExpanded((s) => {
      const next = new Set(s);
      next.delete(path);
      return next;
    });
  }, []);

  const toggle = useCallback(
    (path: string): void => {
      if (expanded.has(path)) collapse(path);
      else expand(path);
    },
    [expanded, expand, collapse],
  );

  // Flatten the currently-visible tree (depth-first, honouring expansion) into
  // the linear order used for ArrowUp/ArrowDown navigation.
  const visible = useMemo<VisibleNode[]>(() => {
    const out: VisibleNode[] = [];
    const walk = (entries: DirEntry[], level: number, parentPath: string | null): void => {
      for (const entry of entries) {
        out.push({ entry, level, parentPath });
        if (entry.isDir && expanded.has(entry.path)) {
          const kids = children.get(entry.path);
          if (kids) walk(kids, level + 1, entry.path);
        }
      }
    };
    if (roots) walk(roots, 1, null);
    return out;
  }, [roots, expanded, children]);

  /**
   * Refs mirroring `visible`, `expanded`, and the workspace-roots list so the
   * OS-drag handlers below can read the freshest snapshot without re-
   * subscribing to the platform bridge on every drag tick or root change.
   */
  const visibleRef = useRef(visible);
  const expandedRef = useRef(expanded);
  const workspaceRootsRef = useRef(workspaceRoots);

  /**
   * True when `target` is the same as, or a descendant of, any path
   * currently in the shared drag store. Drop-targets that match are
   * disabled so a folder can't be dropped into itself (which would hang
   * the move). Reads via `getState()` so the callback identity stays
   * stable across drag ticks.
   */
  const isDropDisabled = useCallback((target: string): boolean => {
    const sources = useFsDragStore.getState().sources;
    if (sources.length === 0) return false;
    return sources.some((s) => isUnderRoot(target, s));
  }, []);
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);
  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);
  useEffect(() => {
    workspaceRootsRef.current = workspaceRoots;
  }, [workspaceRoots]);

  /**
   * Pick the drag mode for a paste batch given source paths + Alt state,
   * taking the target tree's rootPath into account so cross-root drops obey
   * the same-hierarchy rule:
   *
   *   - Source lives under THIS tree's root → in-tree → default = `cut`.
   *   - Source lives under a DIFFERENT opened root that's an ancestor /
   *     descendant of this tree's root → same hierarchy → default = `cut`.
   *   - Source lives under an UNRELATED opened root (sibling branches,
   *     different projects) → default = `copy`.
   *   - Source outside every opened root (e.g. dragged from Finder) →
   *     default = `copy`.
   *
   * The batch defaults to `cut` only when EVERY source resolves to a
   * related root; any unrelated / external source downgrades the whole
   * batch to `copy`. Alt inverts the default.
   */
  const resolveDragMode = useCallback(
    (sources: readonly string[], alt: boolean): 'cut' | 'copy' => {
      const roots = workspaceRootsRef.current;
      const allRelated =
        sources.length > 0 &&
        sources.every((p) => {
          // Fast path: source already lives under THIS tree's root. Keeps
          // the resolver self-contained when workspaceRoots isn't seeded
          // (e.g. unit tests that mount FileTree directly).
          if (isUnderRoot(p, rootPath)) return true;
          // Otherwise: find the longest-prefix opened root that contains
          // the source.
          let srcRoot: string | null = null;
          for (const r of roots) {
            const rp = r.path ?? r.id;
            if (!rp) continue;
            if (isUnderRoot(p, rp)) {
              if (srcRoot === null || rp.length > srcRoot.length) srcRoot = rp;
            }
          }
          if (srcRoot === null) return false; // external source
          // Same hierarchy: srcRoot is the same as, an ancestor of, or a
          // descendant of this tree's root.
          return (
            srcRoot === rootPath ||
            isUnderRoot(srcRoot, rootPath) ||
            isUnderRoot(rootPath, srcRoot)
          );
        });
      const def: 'cut' | 'copy' = allRelated ? 'cut' : 'copy';
      if (!alt) return def;
      return def === 'cut' ? 'copy' : 'cut';
    },
    [rootPath],
  );

  /**
   * Ref-backed drop handler. Filled in by Phase C below (the effect that
   * defines `runOsDrop`); Phase B's subscription just calls through. Using a
   * ref keeps the subscription stable so onDrag/onDrop don't re-subscribe
   * when paste-related state changes. `files` is provided on web (where the
   * bridge can't read paths off disk) and null on desktop.
   */
  const osDropHandlerRef = useRef<
    | ((
        paths: string[],
        files: File[] | undefined,
        targetDir: string,
        mode: 'cut' | 'copy',
      ) => Promise<void>)
    | null
  >(null);
  /**
   * Ref-backed forwarder to the in-tree `pasteInto` callback, so the
   * imperative handle (`pasteIntoRoot`) — declared *above* `pasteInto`
   * for ordering reasons — can call it. Wired in an effect below.
   */
  const pasteIntoRef = useRef<((targetDir: string) => Promise<void>) | null>(null);

  /**
   * Basenames currently in a folder's listing — used by the duplicate-slot
   * picker and the New-File / New-Folder placeholder picker. Falls back to
   * an empty set when the listing isn't loaded.
   */
  const siblingSet = useCallback(
    (parentPath: string): Set<string> => {
      const list = parentPath === rootPath ? roots : children.get(parentPath);
      return new Set((list ?? []).map((e) => e.name));
    },
    [children, roots, rootPath],
  );

  const openFile = useCallback(
    async (entry: DirEntry): Promise<void> => {
      // Unreadable (binary/unsupported) files can't be displayed — no-op.
      if (!isReadableFile(entry.name)) return;
      try {
        // Routes on type: images open in the read-only viewer, everything else
        // as a text/markdown/code document.
        await openPath(platform, entry.path);
      } catch {
        /* a failed read leaves the tree untouched */
      }
    },
    [platform],
  );

  // Focus a row by path, both updating roving tabindex and moving DOM focus.
  const focusRow = useCallback((path: string): void => {
    setFocusedPath(path);
    const el = rowRefs.current.get(path);
    if (el) el.focus();
    else pendingFocus.current = path; // not yet rendered (e.g. just expanded)
  }, []);

  // After a render where we expanded a folder, focus a now-rendered child.
  useEffect(() => {
    const want = pendingFocus.current;
    if (want != null) {
      const el = rowRefs.current.get(want);
      if (el) {
        pendingFocus.current = null;
        el.focus();
      }
    }
    // A pending FS-op focus (e.g. just-created file) waits for the row to
    // appear in the listing after refreshDir.
    const fsWant = pendingFsFocus.current;
    if (fsWant != null) {
      const el = rowRefs.current.get(fsWant);
      if (el) {
        pendingFsFocus.current = null;
        setFocusedPath(fsWant);
        el.focus();
      }
    }
  });

  // ─────────── speed search (type-to-highlight, spec §3-§6) ───────────
  // Owns the query + idle timer + match set; FileTree applies the focus moves.
  // `onJumpToMatch` = focusRow so the query-change "jump to first match" rule
  // (spec §5) lands focus on the matching row via the roving tabindex.
  const speed = useTreeSpeedSearch({ visible, focusedPath, onJumpToMatch: focusRow });

  // Collapse-relocates-focus (spec §5): when `visible` changes while a query is
  // active AND the focused row is no longer visible (an ancestor was
  // collapsed), move focus to — in order — the nearest still-visible ANCESTOR
  // of the old focused path, else the FIRST match, else the
  // previously-adjacent visible row. Expanding never relocates (the focused row
  // stays visible). Keyed on `visible` (NOT query) so it's the structural
  // counterpart to the hook's query-keyed jump; together they cover both ways
  // the match set can shift. We track the prior visible list to recover the
  // old neighbour ordering.
  const prevVisibleRef = useRef<VisibleNode[]>(visible);
  useEffect(() => {
    const prev = prevVisibleRef.current;
    prevVisibleRef.current = visible;
    if (!speed.active) return;
    const cur = focusedPath;
    if (!cur) return;
    if (visible.some((n) => n.entry.path === cur)) return; // still visible → keep

    // The focused row vanished. Find the old node so we can walk its ancestry
    // and recover its old neighbours from the PREVIOUS visible list.
    const visiblePaths = new Set(visible.map((n) => n.entry.path));
    const oldNode = prev.find((n) => n.entry.path === cur);

    // 1. Nearest still-visible ancestor (walk parentPath up the old list).
    let target: string | null = null;
    let ancestor = oldNode?.parentPath ?? null;
    const byPath = new Map(prev.map((n) => [n.entry.path, n]));
    while (ancestor) {
      if (visiblePaths.has(ancestor)) {
        target = ancestor;
        break;
      }
      ancestor = byPath.get(ancestor)?.parentPath ?? null;
    }

    // 2. Else the first match.
    if (target === null && speed.matches.length > 0) {
      target = speed.matches[0];
    }

    // 3. Else the previously-adjacent visible row (prev neighbour, then next),
    //    walking outward until one is still visible.
    if (target === null) {
      const oldIdx = prev.findIndex((n) => n.entry.path === cur);
      if (oldIdx >= 0) {
        for (let d = 1; d < prev.length; d++) {
          const before = prev[oldIdx - d];
          if (before && visiblePaths.has(before.entry.path)) {
            target = before.entry.path;
            break;
          }
          const after = prev[oldIdx + d];
          if (after && visiblePaths.has(after.entry.path)) {
            target = after.entry.path;
            break;
          }
          if (!before && !after) break;
        }
      }
    }

    if (target !== null) focusRow(target);
    else setFocusedPath(null);
    // `speed.matches` is read for fallback #2 but intentionally OMITTED from
    // deps — this effect is the `visible`-change handler; matches recompute in
    // lockstep with `visible`, and adding it would re-run on unrelated query
    // edits (already handled by the hook's query-keyed jump).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // ─────────── inline-rename / inline-create handlers ───────────

  const startRename = useCallback((entry: DirEntry): void => {
    setCreate(null);
    setRename({ path: entry.path, value: entry.name });
  }, []);

  /**
   * Begin inline-create under `parentPath`. The synthetic row is placed at
   * the top of the folder's children once it's expanded. We expand+load
   * first so the input renders inside the expanded folder.
   */
  const startCreate = useCallback(
    (parentPath: string, kind: 'file' | 'folder'): void => {
      setRename(null);
      // Make sure the target folder is open so the inline-create row is
      // visible. expand() loads children lazily; loadChildren is idempotent.
      // The root has no expansion state (its listing is `roots`, not a
      // `children.get(rootPath)`), so we skip the expand step there — the
      // root-level create row is rendered specially at the top of the tree.
      if (parentPath !== rootPath && !expanded.has(parentPath)) {
        setExpanded((s) => new Set(s).add(parentPath));
        loadChildren(parentPath);
      }
      const placeholder = pickUntitledName(kind, siblingSet(parentPath));
      setCreate({ parentPath, kind, value: placeholder });
    },
    [expanded, loadChildren, siblingSet, rootPath],
  );

  // The imperative handle is declared further down — it needs
  // `applyDragHover` and `resolveDragMode` in scope to call into the
  // shared drag-hover machinery defined after `treeRootRef`.

  // Container ref for the tree root, used by the global-keydown listener so
  // shortcuts only fire when focus is inside *this* tree (multi-root sidebars
  // mount several FileTrees).
  const treeRootRef = useRef<HTMLDivElement | null>(null);

  // ─────────── Shared drag-hover visual machinery ───────────
  // The OS drag-in (platform.onDropFiles) AND the internal HTML5 row drag
  // both feed into the SAME visual feedback — folder highlight, sidebar
  // accent ring, springloaded auto-expand, cursor "Copy/Move" badge. We
  // expose the helpers at component scope so two effects (OS subscription
  // + document-level dragover for HTML5) can share them.

  // Springload timer state, kept in a ref so it survives effect re-creation.
  const springloadRef = useRef<{ timer: number | null; path: string | null }>({
    timer: null,
    path: null,
  });

  const clearSpringload = useCallback((): void => {
    const s = springloadRef.current;
    if (s.timer !== null) window.clearTimeout(s.timer);
    springloadRef.current = { timer: null, path: null };
  }, []);

  const armSpringload = useCallback(
    (path: string): void => {
      const s = springloadRef.current;
      if (s.path === path && s.timer !== null) return; // already armed for this row
      clearSpringload();
      const timer = window.setTimeout(() => {
        if (springloadRef.current.path !== path) return; // moved away
        if (expandedRef.current.has(path)) return;
        setExpanded((s2) => new Set(s2).add(path));
        loadChildren(path);
        springloadRef.current = { timer: null, path: null };
      }, 500);
      springloadRef.current = { path, timer };
    },
    [clearSpringload, loadChildren],
  );

  // `data-mp-drag-active` on <html> drives the sidebar-wide accent ring via CSS.
  const setDragActiveAttr = useCallback((active: boolean): void => {
    if (typeof document === 'undefined') return;
    if (active) document.documentElement.dataset.mpDragActive = '';
    else delete document.documentElement.dataset.mpDragActive;
  }, []);

  /**
   * Hit-test `(x, y)` in window CSS-px against THIS tree. Matches a specific
   * row first; falls back to the tree's own root-zone (empty area inside
   * `.mp-tree`, plus the Sidebar group container which is tagged with
   * `data-tree-root-path`). Returns null when the cursor is not over a part
   * of this tree.
   */
  const hitTestForDrag = useCallback(
    (
      x: number,
      y: number,
    ): { targetDir: string; highlightPath: string; rowIsDir: boolean } | null => {
      if (typeof document === 'undefined') return null;
      const root = treeRootRef.current;
      if (!root) return null;
      const el = document.elementFromPoint(x, y);
      if (!el) return null;
      const row = el.closest('[data-tree-path]') as HTMLElement | null;
      if (row && root.contains(row)) {
        const path = row.dataset.treePath;
        if (path) {
          const node = visibleRef.current.find((n) => n.entry.path === path);
          if (node) {
            if (node.entry.isDir) {
              return { targetDir: node.entry.path, highlightPath: node.entry.path, rowIsDir: true };
            }
            const parent = node.parentPath ?? rootPath;
            return { targetDir: parent, highlightPath: parent, rowIsDir: false };
          }
        }
      }
      const rootEl = el.closest('[data-tree-root-path]') as HTMLElement | null;
      if (rootEl && rootEl.dataset.treeRootPath === rootPath) {
        return { targetDir: rootPath, highlightPath: rootPath, rowIsDir: true };
      }
      return null;
    },
    [rootPath],
  );

  /**
   * Apply a single drag-hover tick: sync source paths (OS path supplies
   * them; HTML5 path leaves undefined and we read the ref), hit-test,
   * update dragOverPath / cursor badge, and arm springload for collapsed
   * folder targets.
   */
  const applyDragHover = useCallback(
    (
      x: number,
      y: number,
      alt: boolean,
      paths: readonly string[] | undefined,
    ): void => {
      setDragActiveAttr(true);
      // Only mirror `paths` into the shared drag store when the bridge
      // ACTUALLY knows them (OS drag-in). For in-webview drags Tauri
      // forwards `paths: []` each tick — overwriting with empty there
      // would wipe the sources the row's HTML5 dragstart already set.
      if (paths !== undefined && paths.length > 0) {
        const snap = useFsDragStore.getState().sources;
        const changed =
          snap.length !== paths.length || snap.some((p, i) => p !== paths[i]);
        if (changed) useFsDragStore.getState().setSources(paths);
      }
      const hit = hitTestForDrag(x, y);
      if (!hit) {
        setDragOverPath(null);
        setDragBadge(null);
        clearSpringload();
        return;
      }
      if (isDropDisabled(hit.targetDir)) {
        setDragOverPath(null);
        // Keep the badge visible but neutral — user gets visual feedback
        // that the cursor is in the tree, even on a forbidden target.
        setDragBadge((cur) => (cur ? cur : { mode: 'copy', x, y }));
        clearSpringload();
        return;
      }
      setDragOverPath((cur) => (cur === hit.highlightPath ? cur : hit.highlightPath));
      // Tauri forwards `paths: []` for in-webview drags — fall back to the
      // shared store there so `resolveDragMode` sees the real source list.
      const knownPaths =
        paths && paths.length > 0 ? paths : useFsDragStore.getState().sources;
      const resolved = resolveDragMode(knownPaths, alt);
      const mode: 'copy' | 'move' = resolved === 'cut' ? 'move' : 'copy';
      setDragBadge((cur) =>
        cur && cur.mode === mode && cur.x === x && cur.y === y ? cur : { mode, x, y },
      );
      if (hit.rowIsDir && !expandedRef.current.has(hit.targetDir)) {
        armSpringload(hit.targetDir);
      } else {
        clearSpringload();
      }
    },
    [
      setDragActiveAttr,
      hitTestForDrag,
      isDropDisabled,
      resolveDragMode,
      armSpringload,
      clearSpringload,
    ],
  );

  /**
   * Clear THIS tree's local drag visuals. Does NOT touch the SHARED drag
   * store — cross-root drags depend on every mounted tree being able to
   * read the source list, and clearing eagerly from a non-consuming tree
   * would steal sources before the actual target tree gets to read them.
   * The shared store is cleared via {@link clearSharedDragState}, called
   * only when this tree owns the drop or from the dragend safety-net.
   */
  const clearDragVisuals = useCallback((): void => {
    setDragActiveAttr(false);
    setDragOverPath(null);
    setDragBadge(null);
    clearSpringload();
  }, [setDragActiveAttr, clearSpringload]);

  /** Clear the SHARED store + the global drag-active accent. */
  const clearSharedDragState = useCallback((): void => {
    useFsDragStore.getState().clearSources();
    setDragActiveAttr(false);
  }, [setDragActiveAttr]);

  // (Internal HTML5 drag visuals are driven directly from the row /
  // tree-container / sidebar-group onDragOver handlers, which call
  // `applyDragHover(e.clientX, e.clientY, e.altKey, undefined)`. A previous
  // attempt to bridge HTML5 drags into the same pipeline via a
  // document-level capture-phase listener was unreliable on macOS WKWebView
  // — handlers attached via addEventListener didn't always receive the
  // browser's drag events, so the badge / accent / springload never
  // appeared. The per-handler call ensures visual updates run on the same
  // React event the row's preventDefault uses, which has been verified to
  // fire reliably.)

  // ─────────── OS drag-in (platform.onDropFiles) ───────────
  // The Tauri / Web bridge delivers position + modifier ticks for files
  // dragged in from outside the app (Finder, another browser tab). We feed
  // each tick into the same applyDragHover used by HTML5 internal drags
  // so the visual feedback is uniform; the drop runner does the actual
  // copy / move via pasteSources (desktop) or writeBinary (web).
  useEffect(() => {
    if (typeof platform.onDropFiles !== 'function') return;
    if (!platform.capabilities.fsMutations) return;
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    platform
      .onDropFiles({
        onDrag: (e) => {
          applyDragHover(e.position.x, e.position.y, e.modifiers.alt, e.paths);
        },
        onLeave: () => {
          // Cursor exits the window — drag is over. Safe to clear shared
          // state too (no other tree will need the sources).
          clearDragVisuals();
          clearSharedDragState();
        },
        onDrop: (e) => {
          const hit = hitTestForDrag(e.position.x, e.position.y);
          if (!hit) {
            // The cursor is NOT over this tree. Some OTHER mounted tree
            // (different opened root) may be the actual target — leave
            // the shared store alone so its onDrop can still read it.
            // Per-tree local visuals were already null from onDrag.
            return;
          }
          const blocked = isDropDisabled(hit.targetDir);
          // Effective sources: bridge-supplied (OS drag-in) OR in-app
          // tracked via the shared store (in-webview drag where Tauri
          // forwards empty paths).
          const sources =
            e.paths.length > 0 ? e.paths : [...useFsDragStore.getState().sources];
          // We own this drop — safe to clear both local visuals AND the
          // shared store now.
          clearDragVisuals();
          clearSharedDragState();
          if (blocked) return true; // consume but do nothing
          const mode = resolveDragMode(sources, e.modifiers.alt);
          const run = osDropHandlerRef.current;
          if (run && (sources.length > 0 || (e.files && e.files.length > 0))) {
            void run(sources, e.files, hit.targetDir, mode);
          }
          // Consume the event so the App's onFileDrop "open" handler does
          // not also fire for the same drop.
          return true;
        },
      })
      .then((unsub) => {
        if (cancelled) unsub();
        else unsubscribe = unsub;
      })
      .catch((err) => {
        console.debug('[slate] onDropFiles subscription failed', err);
      });
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [
    platform,
    applyDragHover,
    clearDragVisuals,
    clearSharedDragState,
    hitTestForDrag,
    isDropDisabled,
    resolveDragMode,
  ]);

  // Expose the imperative API used by the sidebar's root-group header. Placed
  // here so it can reference `applyDragHover` / `resolveDragMode` (declared
  // alongside the shared drag-hover machinery above).
  useImperativeHandle(
    ref,
    () => ({
      startCreateInRoot: (kind) => startCreate(rootPath, kind),
      pasteIntoRoot: async () => {
        const fn = pasteIntoRef.current;
        if (!fn) return;
        await fn(rootPath);
      },
      dropAtRoot: async (sources, alt) => {
        const run = osDropHandlerRef.current;
        if (!run) return;
        const effective =
          sources.length > 0 ? sources : [...useFsDragStore.getState().sources];
        if (effective.length === 0) return;
        const mode = resolveDragMode(effective, alt);
        await run(effective, undefined, rootPath, mode);
      },
      reportHover: (clientX, clientY, alt) => {
        applyDragHover(clientX, clientY, alt, undefined);
      },
      reportHoverEnd: () => {
        setDragOverPath(null);
        setDragBadge(null);
      },
    }),
    [startCreate, rootPath, resolveDragMode, applyDragHover],
  );

  // When the visible tree changes (e.g. a row was trashed), prune the
  // selection so we don't carry dead paths.
  useEffect(() => {
    const visiblePaths = new Set(visible.map((n) => n.entry.path));
    setSelection((s) => pruneSelection(s, visiblePaths));
  }, [visible]);

  const commitRename = useCallback(async (): Promise<void> => {
    if (!rename) return;
    const { path: oldPath, value } = rename;
    const trimmed = value.trim();
    setRename(null);
    if (trimmed === '' || trimmed === basename(oldPath)) return; // no-op
    const newPath = await renameNode(fsCtx, oldPath, trimmed);
    if (!newPath) return;
    // Journal the rename so Cmd-Z can flip it back.
    useFsUndoStore.getState().push({ kind: 'rename', from: oldPath, to: newPath });
    // Re-anchor open doc tabs + workspace roots that point at the renamed
    // file (or anywhere under a renamed folder) so unsaved buffers keep
    // applying to the new path — no tab churn, no fresh-load feel. Done
    // BEFORE the tree refresh so the FS watcher's stale-id events for
    // `oldPath` get harmlessly dropped (no doc carries that id anymore).
    useDocStore.getState().applyPathRename(oldPath, newPath);
    // Refresh the parent dir so the row reappears under its new name.
    const parent = oldPath.replace(/[\\/][^\\/]*$/, '');
    pendingFsFocus.current = newPath;
    await refreshDir(parent);
  }, [rename, fsCtx, refreshDir]);

  const cancelRename = useCallback((): void => {
    setRename(null);
  }, []);

  const commitCreate = useCallback(async (): Promise<void> => {
    if (!create) return;
    const { parentPath, kind, value } = create;
    const trimmed = value.trim();
    setCreate(null);
    if (trimmed === '') return; // user cleared the field → silently cancel
    const newPath =
      kind === 'file'
        ? await createFileIn(fsCtx, parentPath, trimmed)
        : await createFolderIn(fsCtx, parentPath, trimmed);
    if (!newPath) return;
    useFsUndoStore.getState().push(
      kind === 'file'
        ? { kind: 'create-file', path: newPath }
        : { kind: 'create-folder', path: newPath },
    );
    pendingFsFocus.current = newPath;
    await refreshDir(parentPath);
    // Auto-open a freshly-created file so the user lands inside their new doc.
    if (kind === 'file') {
      void openPath(platform, newPath).catch(() => {
        /* a failed read leaves the tree state untouched */
      });
    }
  }, [create, fsCtx, refreshDir, platform]);

  const cancelCreate = useCallback((): void => {
    setCreate(null);
  }, []);

  /**
   * Right-click "Move to Trash" / Delete key path. Surfaces a confirmation
   * modal before invoking the destructive op; the actual call lives in
   * `runTrashConfirmed` and only fires when the user accepts. Operates on
   * the selection when the triggering row is part of it; otherwise on the
   * single triggering row.
   */
  const trashEntries = useCallback(
    (entries: DirEntry[], parentPaths: (string | null)[]): void => {
      if (entries.length === 0) return;
      setTrashConfirm({
        paths: entries.map((e) => e.path),
        parentPaths: parentPaths.map((p) => p ?? rootPath),
      });
    },
    [rootPath],
  );

  /** Cancel the pending trash confirmation (Esc / backdrop / Cancel button). */
  const cancelTrash = useCallback((): void => {
    setTrashConfirm(null);
  }, []);

  /** Cancel the pending permanent-delete confirmation. */
  const cancelPermanent = useCallback((): void => {
    setPermanentConfirm(null);
  }, []);

  /**
   * Surface the confirmation for the irreversible Shift-Delete path. Same
   * shape as trash but separate state so the dialog wording differs.
   */
  const permanentEntries = useCallback(
    (entries: DirEntry[], parentPaths: (string | null)[]): void => {
      if (entries.length === 0) return;
      setPermanentConfirm({
        paths: entries.map((e) => e.path),
        parentPaths: parentPaths.map((p) => p ?? rootPath),
      });
    },
    [rootPath],
  );

  /**
   * Run the actual trash op after the user confirms. Refreshes the parent
   * dirs and clears focus / selection of the now-gone rows.
   */
  const runTrashConfirmed = useCallback(async (): Promise<void> => {
    const target = trashConfirm;
    if (!target) return;
    setTrashConfirm(null);
    const ok = await trashNodes(fsCtx, target.paths);
    if (!ok) return;
    const goneSet = new Set(target.paths);
    if (focusedPath && goneSet.has(focusedPath)) setFocusedPath(null);
    setSelection((s) => pruneSelection(s, new Set([...s.selected].filter((p) => !goneSet.has(p)))));
    const seen = new Set<string>();
    for (const parent of target.parentPaths) {
      if (seen.has(parent)) continue;
      seen.add(parent);
      await refreshDir(parent);
    }
  }, [trashConfirm, fsCtx, refreshDir, focusedPath]);

  /**
   * Run the permanent-delete op after the user confirms. Irreversible — the
   * undo journal does NOT record this op.
   */
  const runPermanentConfirmed = useCallback(async (): Promise<void> => {
    const target = permanentConfirm;
    if (!target) return;
    setPermanentConfirm(null);
    const ok = await removeNodes(fsCtx, target.paths);
    if (!ok) return;
    const goneSet = new Set(target.paths);
    if (focusedPath && goneSet.has(focusedPath)) setFocusedPath(null);
    setSelection((s) => pruneSelection(s, new Set([...s.selected].filter((p) => !goneSet.has(p)))));
    const seen = new Set<string>();
    for (const parent of target.parentPaths) {
      if (seen.has(parent)) continue;
      seen.add(parent);
      await refreshDir(parent);
    }
  }, [permanentConfirm, fsCtx, refreshDir, focusedPath]);

  /**
   * Resolve the trashed/permanent selection set: if the triggering entry is
   * part of the active selection, batch over the whole selection; otherwise
   * just the one entry. Returns the entries (with parent paths) ready for
   * trash/permanent flow.
   */
  const resolveBatch = useCallback(
    (entry: DirEntry, parentPath: string | null): { entries: DirEntry[]; parents: (string | null)[] } => {
      if (selection.selected.has(entry.path) && selection.selected.size > 1) {
        const entries: DirEntry[] = [];
        const parents: (string | null)[] = [];
        // Walk the visible flattened list to keep batch order stable.
        for (const node of visible) {
          if (selection.selected.has(node.entry.path)) {
            entries.push(node.entry);
            parents.push(node.parentPath);
          }
        }
        return { entries, parents };
      }
      return { entries: [entry], parents: [parentPath] };
    },
    [selection, visible],
  );

  // ─────────── clipboard (cut / copy / paste) ───────────

  const cutSelection = useCallback(
    (entry: DirEntry): void => {
      const paths =
        selection.selected.has(entry.path) && selection.selected.size > 1
          ? [...selection.selected]
          : [entry.path];
      useFsClipboardStore.getState().cut(paths);
      toast(`Cut ${paths.length} item${paths.length === 1 ? '' : 's'}.`);
    },
    [selection],
  );

  const copySelection = useCallback(
    (entry: DirEntry): void => {
      const paths =
        selection.selected.has(entry.path) && selection.selected.size > 1
          ? [...selection.selected]
          : [entry.path];
      useFsClipboardStore.getState().copy(paths);
      toast(`Copied ${paths.length} item${paths.length === 1 ? '' : 's'}.`);
    },
    [selection],
  );

  /** Open the conflict modal and return a Promise that resolves when the user picks. */
  const resolveConflict: PasteConflictResolver = useCallback(
    (prompt) =>
      new Promise<PasteConflictChoice>((resolve) => {
        setConflict({
          prompt,
          resolve: (choice) => {
            setConflict(null);
            resolve(choice);
          },
        });
      }),
    [],
  );

  const pasteInto = useCallback(
    async (targetDir: string): Promise<void> => {
      const store = useFsClipboardStore.getState();
      const sources = [...store.paths];
      const mode = store.mode;
      if (!mode || sources.length === 0) return;
      const successes = await pasteSources(
        fsCtx,
        sources,
        targetDir,
        mode,
        resolveConflict,
        siblingSet(targetDir),
      );
      // Journal: each successful paste-step gets its inverse pushed.
      // Cut/move also re-anchors open doc tabs that point at the moved
      // path(s) — see {@link useDocStore.applyPathRename}.
      const journal = useFsUndoStore.getState();
      const docs = useDocStore.getState();
      for (const step of successes) {
        if (step.mode === 'cut') {
          journal.push({ kind: 'move', from: step.source, to: step.target });
          docs.applyPathRename(step.source, step.target);
        } else {
          journal.push({ kind: 'copy', copyPath: step.target });
        }
      }
      // Cut clears the clipboard after success; copy keeps it for repeat pastes.
      if (mode === 'cut' && successes.length > 0) store.clear();
      // Refresh affected parents (target dir + source parents for cut).
      const dirs = new Set<string>([targetDir]);
      if (mode === 'cut') {
        for (const step of successes) dirs.add(step.source.replace(/[\\/][^\\/]*$/, ''));
      }
      for (const d of dirs) await refreshDir(d);
    },
    [fsCtx, resolveConflict, refreshDir, siblingSet],
  );

  /**
   * Phase C / D runner: handle an OS drag-in that landed on a row owned by
   * this tree.
   *
   * Desktop: `paths` are absolute filesystem paths from outside the
   * workspace — `pasteSources` happily copies/moves them in (only the
   * target is containment-checked). Conflict prompts and the undo journal
   * are reused via `pasteSources`.
   *
   * Web: `paths` are synthetic ids that point at the raw `files`; the
   * bridge has no `fs.copy`, so we read each File's bytes through
   * `arrayBuffer()` and write them via `platform.writeBinary` into the
   * target dir. Move is not possible on web (the browser can't delete a
   * source outside the workspace), so Alt is ignored with an info toast.
   */
  const runOsDrop = useCallback(
    async (
      paths: string[],
      files: File[] | undefined,
      targetDir: string,
      mode: 'cut' | 'copy',
    ): Promise<void> => {
      // ── Desktop path: real filesystem paths through pasteSources ──
      if (platform.fs && platform.capabilities.fsMutations) {
        if (paths.length === 0) return;
        const successes = await pasteSources(
          fsCtx,
          paths,
          targetDir,
          mode,
          resolveConflict,
          siblingSet(targetDir),
        );
        if (successes.length === 0) return;
        // Journal each success so Cmd-Z reverses it (move → move back;
        // copy → delete the copy). Cut/move ALSO re-anchors any open doc
        // whose path matches the moved source so unsaved buffers follow.
        const journal = useFsUndoStore.getState();
        const docs = useDocStore.getState();
        for (const step of successes) {
          if (step.mode === 'cut') {
            journal.push({ kind: 'move', from: step.source, to: step.target });
            docs.applyPathRename(step.source, step.target);
          } else {
            journal.push({ kind: 'copy', copyPath: step.target });
          }
        }
        // Refresh the target dir; for moves, also refresh each source parent
        // so disappearing rows update if the source happens to be inside the
        // workspace too (rare but possible — e.g. drag from one root to another).
        const dirs = new Set<string>([targetDir]);
        if (mode === 'cut') {
          for (const step of successes) dirs.add(step.source.replace(/[\\/][^\\/]*$/, ''));
        }
        for (const d of dirs) await refreshDir(d);
        return;
      }
      // ── Web path: read bytes from the raw File list, write via writeBinary ──
      if (platform.capabilities.binaryWrite && files && files.length > 0) {
        if (mode === 'cut') {
          toast('Move isn’t available on the web — importing as a copy.', { kind: 'info' });
        }
        const siblings = new Set(siblingSet(targetDir));
        let written = 0;
        let failed = 0;
        const journal = useFsUndoStore.getState();
        for (const file of files) {
          // Auto-rename on collision so the drop never silently overwrites;
          // matches the desktop "Keep both" default for a generous v1 UX.
          let name = file.name;
          if (siblings.has(name)) {
            name = pickDuplicateName(name, siblings);
          }
          const targetPath = joinPath(targetDir, name);
          try {
            const bytes = new Uint8Array(await file.arrayBuffer());
            await platform.writeBinary(targetPath, bytes);
            siblings.add(name);
            written++;
            // Treat as a copy in the undo journal — Cmd-Z deletes the imported
            // file (and the underlying FSA handle, via fs.remove on platforms
            // where it exists; web's fs is null so undo is a no-op).
            journal.push({ kind: 'copy', copyPath: targetPath });
          } catch (err) {
            failed++;
            console.debug('[slate] writeBinary failed for', targetPath, err);
          }
        }
        if (written > 0) {
          const tail = failed > 0 ? ` (${failed} failed)` : '';
          toast(
            `Imported ${written} file${written === 1 ? '' : 's'} into "${basename(targetDir)}/"${tail}.`,
          );
          await refreshDir(targetDir);
        } else if (failed > 0) {
          toast('Drag-import failed.', { kind: 'error' });
        }
        return;
      }
      toast('Drag-import isn’t available on this platform yet.', { kind: 'info' });
    },
    [platform, fsCtx, resolveConflict, refreshDir, siblingSet],
  );

  // Keep the subscription's ref-callback in sync with the latest runOsDrop
  // closure so Phase B's onDrop dispatch always sees fresh deps without
  // triggering a re-subscribe.
  useEffect(() => {
    osDropHandlerRef.current = runOsDrop;
    return () => {
      osDropHandlerRef.current = null;
    };
  }, [runOsDrop]);

  // Mirror runOsDrop's ref-pinning: keep `pasteIntoRef` pointed at the
  // freshest `pasteInto` so the imperative `pasteIntoRoot` (declared
  // earlier, ordering-wise) sees the same closure tree-row Paste uses.
  useEffect(() => {
    pasteIntoRef.current = pasteInto;
    return () => {
      pasteIntoRef.current = null;
    };
  }, [pasteInto]);

  // ─────────── undo (Cmd-Z) ───────────

  const undo = useCallback(async (): Promise<void> => {
    const entry = await undoLast(fsCtx);
    if (!entry) return;
    // Mirror the FS undo on open doc refs + workspace roots so unsaved tabs
    // follow the path back. `move` is also handled here — both rename and
    // move are path swaps from the docStore's perspective.
    if (entry.kind === 'rename') {
      useDocStore.getState().applyPathRename(entry.to, entry.from);
    } else if (entry.kind === 'move') {
      useDocStore.getState().applyPathRename(entry.to, entry.from);
    }
    for (const parent of undoAffectedParents(entry)) {
      await refreshDir(parent);
    }
  }, [fsCtx, refreshDir]);

  // ─────────── global Cmd-X / Cmd-C / Cmd-V / Cmd-Z / Esc on the tree ───────────
  useEffect(() => {
    if (!platform.capabilities.fsMutations) return;
    const onKey = (e: globalThis.KeyboardEvent): void => {
      const root = treeRootRef.current;
      if (!root || !root.contains(document.activeElement)) return;
      // Don't hijack keys while the user is typing into a rename/create input.
      if (document.activeElement?.tagName === 'INPUT') return;

      if (e.key === 'Escape') {
        // Esc clears a "cut" state (the dim disappears) and selection.
        const clip = useFsClipboardStore.getState();
        if (clip.mode === 'cut') clip.clear();
        if (selection.selected.size > 0) setSelection(EMPTY_SELECTION);
        return;
      }
      if (!modPressed(e)) return;
      const key = e.key.toLowerCase();
      if (key !== 'x' && key !== 'c' && key !== 'v' && key !== 'z') return;
      e.preventDefault();
      e.stopPropagation();

      const focusedEntry = (() => {
        if (!focusedPath) return null;
        const node = visible.find((n) => n.entry.path === focusedPath);
        return node ?? null;
      })();
      if (key === 'x') {
        const paths =
          selection.selected.size > 0
            ? [...selection.selected]
            : focusedEntry
              ? [focusedEntry.entry.path]
              : [];
        if (paths.length === 0) return;
        useFsClipboardStore.getState().cut(paths);
        toast(`Cut ${paths.length} item${paths.length === 1 ? '' : 's'}.`);
      } else if (key === 'c') {
        const paths =
          selection.selected.size > 0
            ? [...selection.selected]
            : focusedEntry
              ? [focusedEntry.entry.path]
              : [];
        if (paths.length === 0) return;
        useFsClipboardStore.getState().copy(paths);
        toast(`Copied ${paths.length} item${paths.length === 1 ? '' : 's'}.`);
      } else if (key === 'v') {
        const target = focusedEntry
          ? focusedEntry.entry.isDir
            ? focusedEntry.entry.path
            : focusedEntry.parentPath ?? rootPath
          : rootPath;
        void pasteInto(target);
      } else if (key === 'z' && !e.shiftKey) {
        void undo();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [platform, focusedPath, visible, selection, rootPath, pasteInto, undo]);

  const duplicateEntry = useCallback(
    async (entry: DirEntry, parentPath: string | null): Promise<void> => {
      const parent = parentPath ?? rootPath;
      const newPath = await duplicateNode(fsCtx, entry.path, siblingSet(parent));
      if (!newPath) return;
      useFsUndoStore.getState().push({ kind: 'duplicate', copyPath: newPath });
      pendingFsFocus.current = newPath;
      await refreshDir(parent);
    },
    [fsCtx, refreshDir, rootPath, siblingSet],
  );

  const activate = useCallback(
    (node: VisibleNode): void => {
      if (node.entry.isDir) toggle(node.entry.path);
      else void openFile(node.entry);
    },
    [toggle, openFile],
  );

  // Build the context-menu item set for a row. FILE: Open, Rename, Duplicate,
  // Move to Trash, Copy path. FOLDER: New File / New Folder, Rename, Duplicate,
  // Move to Trash, Expand/Collapse, Copy path.
  const buildMenu = useCallback(
    (entry: DirEntry, parentPath: string | null): ContextMenuEntry[] => {
      const copyPath = async (): Promise<void> => {
        try {
          await platform.writeClipboardText(entry.path);
        } catch {
          /* clipboard write unavailable — no-op */
        }
      };
      // Append tool-contributed directory items (e.g. terminal's "Open Terminal
      // Here") to a folder menu. Gated on `toolHost` — without a host the slice
      // still renders, just without the tool entries. Honors each item's
      // `visible(args, host)` filter and resolves its string icon name.
      const appendToolMenuItems = (target: ContextMenuEntry[], path: string): void => {
        if (!toolHost || dirContributions.length === 0) return;
        const args = { path };
        const visible = dirContributions.filter(
          (c) => c.visible === undefined || c.visible(args, toolHost),
        );
        if (visible.length === 0) return;
        target.push({ type: 'separator', id: 'sep-tools' });
        for (const c of visible) {
          const label = typeof c.label === 'function' ? c.label(args) : c.label;
          target.push({
            id: c.id,
            label,
            icon: resolveContributionIcon(c.icon),
            onSelect: () => {
              void c.run(args, toolHost);
            },
          });
        }
      };
      const canMutate = platform.capabilities.fsMutations === true;
      const canPaste = clipboardMode != null && clipboardPaths.length > 0;
      const canUndo = useFsUndoStore.getState().entries.length > 0;
      const trashLabel =
        selection.selected.has(entry.path) && selection.selected.size > 1
          ? `Move ${selection.selected.size} items to Trash`
          : 'Move to Trash';
      const permanentLabel =
        selection.selected.has(entry.path) && selection.selected.size > 1
          ? `Delete ${selection.selected.size} items Permanently…`
          : 'Delete Permanently…';
      // Run trash/permanent on the active batch (selection if it covers
      // entry; else just entry).
      const doTrash = (): void => {
        const { entries, parents } = resolveBatch(entry, parentPath);
        trashEntries(entries, parents);
      };
      const doPermanent = (): void => {
        const { entries, parents } = resolveBatch(entry, parentPath);
        permanentEntries(entries, parents);
      };
      const pasteTarget = entry.isDir ? entry.path : parentPath ?? rootPath;
      if (entry.isDir) {
        const isOpen = expanded.has(entry.path);
        const items: ContextMenuEntry[] = [];
        // "Open as new root" — pins a subfolder as its own workspace root so
        // it appears as a sibling tree in the sidebar. Hidden when the
        // folder is already an opened root (addWorkspaceRoot dedupes by id
        // anyway, but the menu entry would be misleading).
        const isAlreadyRoot = workspaceRoots.some(
          (r) => (r.path ?? r.id) === entry.path,
        );
        if (!isAlreadyRoot) {
          items.push(
            {
              id: 'open-as-root',
              label: 'Open as new root',
              icon: FolderTree,
              onSelect: () => {
                const ref = { id: entry.path, path: entry.path, name: entry.name };
                addWorkspaceRoot(ref);
                void platform.pushRecentWorkspace(ref).catch(() => {});
              },
            },
            { type: 'separator', id: 'sep-open-as-root' },
          );
        }
        if (canMutate) {
          items.push(
            {
              id: 'new-file',
              label: 'New File…',
              icon: FilePlus,
              onSelect: () => startCreate(entry.path, 'file'),
            },
            {
              id: 'new-folder',
              label: 'New Folder…',
              icon: FolderPlus,
              onSelect: () => startCreate(entry.path, 'folder'),
            },
            { type: 'separator', id: 'sep-create' },
            {
              id: 'cut',
              label: 'Cut',
              icon: Scissors,
              shortcut: shortcutLabel('X'),
              onSelect: () => cutSelection(entry),
            },
            {
              id: 'copy',
              label: 'Copy',
              icon: ClipboardCopy,
              shortcut: shortcutLabel('C'),
              onSelect: () => copySelection(entry),
            },
            {
              id: 'paste',
              label: 'Paste',
              icon: ClipboardPaste,
              shortcut: shortcutLabel('V'),
              disabled: !canPaste,
              onSelect: () => void pasteInto(pasteTarget),
            },
            { type: 'separator', id: 'sep-clip' },
            {
              id: 'rename',
              label: 'Rename',
              icon: Pencil,
              shortcut: 'F2',
              onSelect: () => startRename(entry),
            },
            {
              id: 'duplicate',
              label: 'Duplicate',
              icon: Copy,
              onSelect: () => void duplicateEntry(entry, parentPath),
            },
            {
              id: 'trash',
              label: trashLabel,
              icon: Trash2,
              shortcut: keyLabel('Delete'),
              danger: true,
              onSelect: doTrash,
              // Hold Alt/Option to swap the label to "Delete Permanently…" —
              // no shortcut binding, intentionally hidden behind a modifier
              // so the destructive op can't be triggered accidentally.
              altLabel: permanentLabel,
              altIcon: Trash2,
              altDanger: true,
              altOnSelect: doPermanent,
            },
            { type: 'separator', id: 'sep-mutate' },
            {
              id: 'undo',
              label: 'Undo',
              icon: Undo2,
              shortcut: shortcutLabel('Z'),
              disabled: !canUndo,
              onSelect: () => void undo(),
            },
            { type: 'separator', id: 'sep-undo' },
          );
        }
        items.push(
          {
            id: 'toggle',
            label: isOpen ? 'Collapse' : 'Expand',
            icon: isOpen ? ChevronDown : ChevronRight,
            shortcut: isOpen ? '←' : '→',
            onSelect: () => toggle(entry.path),
          },
          { type: 'separator', id: 'sep-end' },
          { id: 'copy-path', label: 'Copy path', icon: Clipboard, onSelect: () => void copyPath() },
        );
        appendToolMenuItems(items, entry.path);
        return items;
      }
      const items: ContextMenuEntry[] = [
        {
          id: 'open',
          label: 'Open',
          icon: FileText,
          shortcut: keyLabel('Enter'),
          onSelect: () => void openFile(entry),
        },
      ];
      if (canMutate) {
        items.push(
          { type: 'separator', id: 'sep-clip' },
          {
            id: 'cut',
            label: 'Cut',
            icon: Scissors,
            shortcut: shortcutLabel('X'),
            onSelect: () => cutSelection(entry),
          },
          {
            id: 'copy',
            label: 'Copy',
            icon: ClipboardCopy,
            shortcut: shortcutLabel('C'),
            onSelect: () => copySelection(entry),
          },
          {
            id: 'paste',
            label: 'Paste',
            icon: ClipboardPaste,
            shortcut: shortcutLabel('V'),
            disabled: !canPaste,
            onSelect: () => void pasteInto(pasteTarget),
          },
          { type: 'separator', id: 'sep-mutate' },
          {
            id: 'rename',
            label: 'Rename',
            icon: Pencil,
            shortcut: 'F2',
            onSelect: () => startRename(entry),
          },
          {
            id: 'duplicate',
            label: 'Duplicate',
            icon: Copy,
            onSelect: () => void duplicateEntry(entry, parentPath),
          },
          {
            id: 'trash',
            label: trashLabel,
            icon: Trash2,
            shortcut: keyLabel('Delete'),
            danger: true,
            onSelect: doTrash,
            altLabel: permanentLabel,
            altIcon: Trash2,
            altDanger: true,
            altOnSelect: doPermanent,
          },
          { type: 'separator', id: 'sep-undo' },
          {
            id: 'undo',
            label: 'Undo',
            icon: Undo2,
            shortcut: shortcutLabel('Z'),
            disabled: !canUndo,
            onSelect: () => void undo(),
          },
        );
      }
      items.push(
        { type: 'separator', id: 'sep-end' },
        { id: 'copy-path', label: 'Copy path', icon: Clipboard, onSelect: () => void copyPath() },
      );
      return items;
    },
    [
      platform,
      expanded,
      toggle,
      openFile,
      startCreate,
      startRename,
      duplicateEntry,
      cutSelection,
      copySelection,
      pasteInto,
      resolveBatch,
      trashEntries,
      permanentEntries,
      undo,
      clipboardMode,
      clipboardPaths,
      selection,
      rootPath,
      workspaceRoots,
      addWorkspaceRoot,
      dirContributions,
      toolHost,
    ],
  );

  // Open the menu for a row at the pointer (mouse) — native menu already
  // suppressed globally, so we just opt in here.
  const onRowContextMenu = useCallback(
    (e: ReactMouseEvent, entry: DirEntry, parentPath: string | null): void => {
      e.preventDefault();
      e.stopPropagation();
      setFocusedPath(entry.path);
      openContextMenu(e.clientX, e.clientY, buildMenu(entry, parentPath));
    },
    [openContextMenu, buildMenu],
  );

  // Touch long-press → open the same menu. Resolve the row under the touch point
  // so a single hook serves every row (rules-of-hooks safe).
  const onLongPressTouch = useCallback(
    (x: number, y: number): void => {
      const el = document.elementFromPoint?.(x, y);
      const row = el?.closest?.('[data-tree-path]') as HTMLElement | null;
      const path = row?.dataset.treePath;
      if (!path) return;
      const node = visible.find((n) => n.entry.path === path);
      if (!node) return;
      setFocusedPath(path);
      openContextMenu(x, y, buildMenu(node.entry, node.parentPath));
    },
    [visible, openContextMenu, buildMenu],
  );
  const longPress = useLongPress(onLongPressTouch);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>, index: number): void => {
      const node = visible[index];
      if (!node) return;
      const { entry } = node;
      const isDir = entry.isDir;
      const isOpen = isDir && expanded.has(entry.path);

      // F2 / Backspace / Delete fire only when no edit is in progress; the
      // inline-rename / inline-create input owns those keys when active.
      const editing = rename !== null || create !== null;
      if (editing) return;

      // Speed search (spec §4): delegate FIRST, before the normal nav switch.
      // It captures printable chars (incl. a Space ONLY while a query is
      // active), Backspace-while-active, Escape-while-active, and Up/Down match
      // cycling while active. The hook already `preventDefault`ed anything it
      // consumed and returns the row to focus (if any). Every other key — and
      // every key when no query is active except a fresh printable char — falls
      // through to the unchanged switch below.
      const ss = speed.handleKey(e);
      if (ss.handled) {
        if (ss.focusPath) focusRow(ss.focusPath);
        return;
      }

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = visible[index + 1];
          if (next) focusRow(next.entry.path);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = visible[index - 1];
          if (prev) focusRow(prev.entry.path);
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          if (!isDir) break; // file → no-op
          if (!isOpen) {
            expand(entry.path);
          } else {
            // Already open → focus the first child (the next visible node, which
            // is a descendant since this folder is expanded). If children aren't
            // loaded yet there is nothing to move to.
            const next = visible[index + 1];
            if (next && next.parentPath === entry.path) focusRow(next.entry.path);
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          if (isOpen) {
            collapse(entry.path);
          } else if (node.parentPath) {
            focusRow(node.parentPath);
          }
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          activate(node);
          break;
        }
        case 'Home': {
          e.preventDefault();
          const first = visible[0];
          if (first) focusRow(first.entry.path);
          break;
        }
        case 'End': {
          e.preventDefault();
          const last = visible[visible.length - 1];
          if (last) focusRow(last.entry.path);
          break;
        }
        case 'F2': {
          e.preventDefault();
          if (platform.capabilities.fsMutations) startRename(entry);
          break;
        }
        case 'Backspace':
        case 'Delete': {
          if (!platform.capabilities.fsMutations) break;
          // Backspace / Delete always go to Trash. Permanent delete is
          // gated to the context menu's Alt-held alternate so it can't
          // be triggered by a stray keypress.
          e.preventDefault();
          const { entries, parents } = resolveBatch(entry, node.parentPath);
          trashEntries(entries, parents);
          break;
        }
        default:
          break;
      }
    },
    [
      visible,
      expanded,
      rename,
      create,
      expand,
      collapse,
      focusRow,
      activate,
      platform,
      startRename,
      resolveBatch,
      trashEntries,
      speed,
    ],
  );

  if (error) return <div className="mp-tree__hint">Couldn’t read this folder.</div>;
  if (roots === null) return <div className="mp-tree__hint">Loading…</div>;
  if (roots.length === 0 && create === null)
    return (
      <div role="tree" aria-label={label} className="mp-tree mp-tree--empty">
        <div className="mp-tree__hint">This folder is empty.</div>
      </div>
    );

  // The single tab stop: the focused row, else the active file if visible, else
  // the first row. Keeps the tree as one entry in the document tab order.
  const tabbablePath =
    (focusedPath && visible.some((n) => n.entry.path === focusedPath) ? focusedPath : null) ??
    (visible.some((n) => n.entry.path === activeId) ? activeId : null) ??
    visible[0]?.entry.path ??
    null;

  const rootCreate = create && create.parentPath === rootPath ? create : null;

  return (
    <>
    <div
      ref={treeRootRef}
      className="mp-tree"
      role="tree"
      aria-label={label}
      data-tree-root-path={rootPath}
      data-root-drop-target={dragOverPath === rootPath ? '' : undefined}
      onBlur={(e) => {
        // Clear the speed-search query when focus leaves the tree entirely
        // (spec §6). `onBlur` bubbles (focusout); `relatedTarget` is where
        // focus is going — null (e.g. tab closed) or any node outside this
        // tree means we've left. Moving between rows keeps the query.
        if (!speed.active) return;
        const next = e.relatedTarget as Node | null;
        if (next && e.currentTarget.contains(next)) return;
        speed.reset();
      }}
      onTouchStart={longPress.onTouchStart}
      onTouchMove={longPress.onTouchMove}
      onTouchEnd={longPress.onTouchEnd}
      onTouchCancel={longPress.onTouchCancel}
      onDragOver={(e) => {
        // Child rows stopPropagation when they handle the drag, so this
        // handler fires for hovers over the tree's empty area.
        const types = e.dataTransfer.types;
        const hasMime =
          !!types && (types.includes('application/x-slate-fs') || types.includes('Files'));
        const hasInApp = useFsDragStore.getState().sources.length > 0;
        if (!hasMime && !hasInApp) return;
        // Same shared visual update used by the OS drag-in path.
        applyDragHover(e.clientX, e.clientY, e.altKey, undefined);
        if (isDropDisabled(rootPath)) {
          e.dataTransfer.dropEffect = 'none';
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = e.altKey ? 'copy' : 'move';
      }}
      onDragLeave={(e) => {
        // Only clear hover visuals when the cursor truly leaves the tree
        // container — do NOT touch `dragSources` here, the drag is still
        // in flight; the source row stays dimmed and the ref stays
        // populated so dropping anywhere else in the sidebar still works.
        if (e.currentTarget !== e.target) return;
        setDragOverPath(null);
        setDragBadge(null);
      }}
      onDrop={(e) => {
        const internal = e.dataTransfer.getData('application/x-slate-fs');
        let sources: string[] = [];
        if (internal) {
          try { sources = JSON.parse(internal); } catch { sources = []; }
        }
        if (sources.length === 0) {
          const ref = useFsDragStore.getState().sources;
          if (ref.length > 0) sources = [...ref];
        }
        if (sources.length === 0) return; // OS drops route through the bridge
        e.preventDefault();
        e.stopPropagation();
        setDragOverPath(null);
        if (isDropDisabled(rootPath)) return;
        const mode = resolveDragMode(sources, e.altKey);
        void (async () => {
          const ok = await pasteSources(
            fsCtx,
            sources,
            rootPath,
            mode,
            resolveConflict,
            siblingSet(rootPath),
          );
          const journal = useFsUndoStore.getState();
          const docs = useDocStore.getState();
          for (const step of ok) {
            if (step.mode === 'cut') {
              journal.push({ kind: 'move', from: step.source, to: step.target });
              docs.applyPathRename(step.source, step.target);
            } else {
              journal.push({ kind: 'copy', copyPath: step.target });
            }
          }
          const dirs = new Set<string>([rootPath]);
          if (mode === 'cut')
            for (const s of ok) dirs.add(s.source.replace(/[\\/][^\\/]*$/, ''));
          for (const d of dirs) await refreshDir(d);
        })();
      }}
    >
      {/* Speed-search floating label (spec §7): a small pill pinned to the
          top-left of the tree pane, echoing the live query. `data-no-match`
          paints it muted-red when nothing matches; `aria-live="polite"`
          announces the query / no-match state. May overlay the first rows —
          acceptable, IntelliJ does the same. */}
      {speed.active && (
        <div
          className="mp-tree__speedsearch"
          data-no-match={speed.matches.length === 0 ? '' : undefined}
          role="status"
          aria-live="polite"
        >
          {speed.query}
        </div>
      )}
      {/* Root-level inline-create row. Lives outside the visible.map loop
          because the root itself has no DOM row to nest beneath. */}
      {rootCreate && (
        <div
          className="mp-tree__row mp-tree__row--file"
          style={{ paddingLeft: 8 }}
          aria-label={`Create new ${rootCreate.kind}`}
        >
          <span className="mp-tree__chevron-gap" aria-hidden />
          {rootCreate.kind === 'folder' ? (
            <Folder size={14} className="mp-tree__icon" aria-hidden />
          ) : (
            <FileText size={14} className="mp-tree__icon" aria-hidden />
          )}
          <InlineEditInput
            value={rootCreate.value}
            onChange={(v) => setCreate((s) => (s ? { ...s, value: v } : s))}
            onCommit={() => void commitCreate()}
            onCancel={cancelCreate}
            ariaLabel={`New ${rootCreate.kind} name`}
            selectOnMount="select"
          />
        </div>
      )}
      {visible.map((node, index) => {
        const { entry, level } = node;
        const isDir = entry.isDir;
        const isOpen = isDir && expanded.has(entry.path);
        const isActive = !isDir && activeId === entry.path;
        const tabbable = entry.path === tabbablePath;
        const isRenaming = rename?.path === entry.path;
        // An expanded folder whose children haven't resolved yet shows an inline
        // hint immediately below it (its children contribute no visible nodes).
        const kids = isOpen ? children.get(entry.path) : undefined;
        const showCreateHere = create !== null && create.parentPath === entry.path && isOpen;
        const hint =
          isOpen && kids === undefined
            ? 'Loading…'
            : isOpen && kids?.length === 0 && !showCreateHere
              ? 'Empty'
              : null;
        // Binary/unsupported files can't be opened — render them greyed out.
        const unreadable = !isDir && !isReadableFile(entry.name);
        const canMutate = platform.capabilities.fsMutations === true;

        const isSelected = selection.selected.has(entry.path);
        const isCut = clipboardSet.has(entry.path);
        const isDropTarget = isDir && dragOverPath === entry.path;
        // The row is part of the in-flight drag's source set → dim it so the
        // user can see what's being moved.
        const isDragSource = dragSources.includes(entry.path);
        // For folders, also flag the row as a self/descendant drop-disabled
        // target — drives CSS that grays out the row and suppresses the
        // accent highlight even if the cursor lands on it.
        const isDropForbidden =
          isDir && dragSources.length > 0 && dragSources.some((s) => isUnderRoot(entry.path, s));
        // Speed-search highlight: the row matches the active query, and the
        // `[start, end)` slice of its name to emphasise (null when no query).
        const isSpeedMatch = speed.matchSet.has(entry.path);
        const matchRangeForRow = speed.active ? speed.rangeFor(entry.name) : null;
        return (
          <Fragment key={entry.path}>
            <div
              ref={(el) => {
                if (el) rowRefs.current.set(entry.path, el);
                else rowRefs.current.delete(entry.path);
              }}
              role="treeitem"
              aria-level={level}
              aria-expanded={isDir ? isOpen : undefined}
              aria-selected={isSelected || isActive}
              tabIndex={tabbable ? 0 : -1}
              className={
                (isDir ? 'mp-tree__row mp-tree__row--dir' : 'mp-tree__row mp-tree__row--file') +
                (isSpeedMatch ? ' mp-tree__row--match' : '')
              }
              data-active={isActive ? '' : undefined}
              data-focused={focusedPath === entry.path ? '' : undefined}
              data-speedsearch-match={isSpeedMatch ? '' : undefined}
              data-selected={isSelected ? '' : undefined}
              data-cut={isCut ? '' : undefined}
              data-drop-target={isDropTarget && !isDropForbidden ? '' : undefined}
              data-drag-source={isDragSource ? '' : undefined}
              data-drop-forbidden={isDropForbidden ? '' : undefined}
              data-unreadable={unreadable ? '' : undefined}
              data-tree-path={entry.path}
              style={{ paddingLeft: 8 + (level - 1) * 11 }}
              title={entry.path}
              draggable={canMutate && !isRenaming}
              onDragStart={(e) => {
                // Drag-source: the active selection (if it covers this row) or
                // just this row. Stashed in the SHARED useFsDragStore so a
                // target FileTree in a different opened root can read it; also
                // mirrored under the app-specific MIME for cross-window /
                // non-Tauri targets that can still see HTML5 dataTransfer.
                const { entries } = resolveBatch(entry, node.parentPath);
                const paths = entries.map((x) => x.path);
                useFsDragStore.getState().setSources(paths);
                try {
                  e.dataTransfer.setData('application/x-slate-fs', JSON.stringify(paths));
                  e.dataTransfer.effectAllowed = 'copyMove';
                } catch {
                  /* some webviews are picky about dataTransfer.setData; degrade gracefully */
                }
              }}
              onDragEnd={() => {
                // CRITICAL: do NOT clear the SHARED drag store here. On
                // macOS WKWebView the HTML5 dragend fires a tick BEFORE
                // Tauri's bridge onDrop event reaches us — clearing
                // dragSources here would leave the target tree's onDrop
                // (potentially in a DIFFERENT opened root) without sources.
                // Clear only this row's local hover state; let the bridge's
                // onDrop on the target tree call clearSharedDragState. A
                // safety-net timeout clears shared state if neither the
                // bridge's onDrop nor onLeave fires (e.g., weird cancel).
                setDragOverPath(null);
                setDragBadge(null);
                clearSpringload();
                window.setTimeout(() => {
                  clearSharedDragState();
                }, 250);
              }}
              onDragOver={(e) => {
                if (!isDir) return;
                const types = e.dataTransfer.types;
                const hasMime =
                  !!types && (types.includes('application/x-slate-fs') || types.includes('Files'));
                const hasInApp = useFsDragStore.getState().sources.length > 0;
                if (!hasMime && !hasInApp) return;
                applyDragHover(e.clientX, e.clientY, e.altKey, undefined);
                if (isDropDisabled(entry.path)) {
                  e.dataTransfer.dropEffect = 'none';
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = e.altKey ? 'copy' : 'move';
              }}
              onDragLeave={(e) => {
                // The drag-leave fires for child elements too; only clear when
                // the cursor genuinely leaves the row container.
                if (e.currentTarget === e.target) setDragOverPath(null);
              }}
              onDrop={(e) => {
                if (!isDir) return;
                // Prefer the dataTransfer payload, but fall back to the ref-
                // tracked sources when the webview stripped the custom MIME
                // (silent-drop bug otherwise). OS file drops are routed by
                // the platform-level onDropFiles subscription elsewhere.
                const internal = e.dataTransfer.getData('application/x-slate-fs');
                let sources: string[] = [];
                if (internal) {
                  try { sources = JSON.parse(internal); } catch { sources = []; }
                }
                if (sources.length === 0) {
                  const ref = useFsDragStore.getState().sources;
                  if (ref.length > 0) sources = [...ref];
                }
                if (sources.length === 0) return;
                e.preventDefault();
                e.stopPropagation();
                setDragOverPath(null);
                // Internal sources are always in-tree → default move; Alt
                // flips to copy. Source-aware resolver keeps the rule
                // consistent with OS drag-in.
                const mode = resolveDragMode(sources, e.altKey);
                void (async () => {
                  const ok = await pasteSources(
                    fsCtx,
                    sources,
                    entry.path,
                    mode,
                    resolveConflict,
                    siblingSet(entry.path),
                  );
                  const journal = useFsUndoStore.getState();
                  const docs = useDocStore.getState();
                  for (const step of ok) {
                    if (step.mode === 'cut') {
                      journal.push({ kind: 'move', from: step.source, to: step.target });
                      docs.applyPathRename(step.source, step.target);
                    } else {
                      journal.push({ kind: 'copy', copyPath: step.target });
                    }
                  }
                  const dirs = new Set<string>([entry.path]);
                  if (mode === 'cut') for (const s of ok) dirs.add(s.source.replace(/[\\/][^\\/]*$/, ''));
                  for (const d of dirs) await refreshDir(d);
                })();
              }}
              onClick={(e) => {
                if (isRenaming) return;
                setFocusedPath(entry.path);
                // Multi-select modifiers: Shift = range from anchor, Cmd/Ctrl =
                // toggle. Plain click replaces and activates.
                if (e.shiftKey) {
                  setSelection((s) =>
                    selectRange(
                      s,
                      entry.path,
                      visible.map((n) => n.entry.path),
                    ),
                  );
                  return;
                }
                if (modPressed(e.nativeEvent)) {
                  setSelection((s) => toggleOne(s, entry.path));
                  return;
                }
                setSelection(selectOnly(entry.path));
                activate(node);
              }}
              onContextMenu={(e) => onRowContextMenu(e, entry, node.parentPath)}
              onKeyDown={(e) => onKeyDown(e, index)}
            >
              {isDir ? (
                <ChevronRight
                  size={13}
                  className="mp-tree__chevron"
                  data-open={isOpen ? '' : undefined}
                  aria-hidden
                />
              ) : (
                <span className="mp-tree__chevron-gap" aria-hidden />
              )}
              <FileTypeIcon
                name={entry.name}
                kind={isDir ? 'folder' : 'file'}
                open={isOpen}
                muted={unreadable}
                size={14}
                className="mp-tree__icon"
              />

              {isRenaming ? (
                <InlineEditInput
                  value={rename.value}
                  onChange={(v) => setRename((s) => (s ? { ...s, value: v } : s))}
                  onCommit={() => void commitRename()}
                  onCancel={cancelRename}
                  ariaLabel={`Rename ${entry.name}`}
                />
              ) : (
                <span className="mp-tree__label">
                  {matchRangeForRow ? (
                    <>
                      {entry.name.slice(0, matchRangeForRow[0])}
                      <mark className="mp-tree__match-hit">
                        {entry.name.slice(matchRangeForRow[0], matchRangeForRow[1])}
                      </mark>
                      {entry.name.slice(matchRangeForRow[1])}
                    </>
                  ) : (
                    entry.name
                  )}
                </span>
              )}

              {/* The "+" affordance lives only on folder rows. Wrapped in
                  stopPropagation so clicking it doesn't toggle the folder. */}
              {isDir && canMutate && !isRenaming && (
                <button
                  type="button"
                  className="mp-tree__plus"
                  aria-label={`Add to ${entry.name}`}
                  title="New file or folder here"
                  data-open={isOpen ? '' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openContextMenu(e.clientX, e.clientY, [
                      {
                        id: 'new-file',
                        label: 'New File…',
                        icon: FilePlus,
                        onSelect: () => startCreate(entry.path, 'file'),
                      },
                      {
                        id: 'new-folder',
                        label: 'New Folder…',
                        icon: FolderPlus,
                        onSelect: () => startCreate(entry.path, 'folder'),
                      },
                    ]);
                  }}
                >
                  <Plus size={13} aria-hidden />
                </button>
              )}
            </div>

            {/* Inline-create row appears as the first child of its parent
                directory. Placed AFTER the parent row so it sits visually
                indented one level deeper, like a real child. */}
            {showCreateHere && create && (
              <div
                className="mp-tree__row mp-tree__row--file"
                style={{ paddingLeft: 8 + level * 11 }}
                aria-label={`Create new ${create.kind}`}
              >
                <span className="mp-tree__chevron-gap" aria-hidden />
                {create.kind === 'folder' ? (
                  <Folder size={14} className="mp-tree__icon" aria-hidden />
                ) : (
                  <FileText size={14} className="mp-tree__icon" aria-hidden />
                )}
                <InlineEditInput
                  value={create.value}
                  onChange={(v) => setCreate((s) => (s ? { ...s, value: v } : s))}
                  onCommit={() => void commitCreate()}
                  onCancel={cancelCreate}
                  ariaLabel={`New ${create.kind} name`}
                  selectOnMount="select"
                />
              </div>
            )}

            {hint && (
              <div
                role="none"
                className="mp-tree__hint"
                style={{ paddingLeft: 8 + level * 11 }}
              >
                {hint}
              </div>
            )}
          </Fragment>
        );
      })}
    </div>

    {trashConfirm && (
      <Modal
        open
        onClose={cancelTrash}
        title={trashConfirm.paths.length === 1 ? 'Move to Trash' : `Move ${trashConfirm.paths.length} items to Trash`}
        actions={[
          // Destructive: Cancel is the Esc target AND the Enter default so the
          // safe choice is what auto-focuses and what Enter triggers.
          { id: 'cancel', label: 'Cancel', onSelect: cancelTrash, accelerator: 'c', isCancel: true, isDefault: true },
          {
            id: 'trash',
            label: 'Move to Trash',
            onSelect: () => void runTrashConfirmed(),
            // Uppercase so the underline lands on the T of "Trash", not the t
            // of "to". Key matching stays case-insensitive (fires on t/T).
            accelerator: 'T',
            variant: 'danger',
          },
        ] satisfies ModalAction[]}
      >
        {trashConfirm.paths.length === 1 ? (
          <p>
            Move <strong>{basename(trashConfirm.paths[0])}</strong> to the system Trash?
            You can restore it later from your OS Trash.
          </p>
        ) : (
          <>
            <p>Move these items to the system Trash?</p>
            <ul className="mp-modal__list">
              {trashConfirm.paths.slice(0, 6).map((p) => (
                <li key={p}>{basename(p)}</li>
              ))}
              {trashConfirm.paths.length > 6 && (
                <li className="mp-modal__hint">…and {trashConfirm.paths.length - 6} more</li>
              )}
            </ul>
            <p className="mp-modal__hint">You can restore them later from your OS Trash.</p>
          </>
        )}
      </Modal>
    )}

    {permanentConfirm && (
      <Modal
        open
        onClose={cancelPermanent}
        title={
          permanentConfirm.paths.length === 1
            ? 'Delete Permanently'
            : `Delete ${permanentConfirm.paths.length} items Permanently`
        }
        actions={[
          // Irreversible delete: Cancel is the Esc target AND the Enter default;
          // the destructive action carries 'd' but is never the default.
          { id: 'cancel', label: 'Cancel', onSelect: cancelPermanent, accelerator: 'c', isCancel: true, isDefault: true },
          {
            id: 'delete',
            label: 'Delete Permanently',
            onSelect: () => void runPermanentConfirmed(),
            accelerator: 'd',
            variant: 'danger',
          },
        ] satisfies ModalAction[]}
      >
        {permanentConfirm.paths.length === 1 ? (
          <p>
            Delete <strong>{basename(permanentConfirm.paths[0])}</strong> permanently?
            This can't be undone.
          </p>
        ) : (
          <>
            <p>Delete these items permanently?</p>
            <ul className="mp-modal__list">
              {permanentConfirm.paths.slice(0, 6).map((p) => (
                <li key={p}>{basename(p)}</li>
              ))}
              {permanentConfirm.paths.length > 6 && (
                <li className="mp-modal__hint">
                  …and {permanentConfirm.paths.length - 6} more
                </li>
              )}
            </ul>
            <p className="mp-modal__hint">This can't be undone.</p>
          </>
        )}
      </Modal>
    )}

    {conflict && (
      <PasteConflictDialog
        prompt={conflict.prompt}
        onResolve={(choice) => conflict.resolve(choice)}
      />
    )}

    {dragBadge && (
      <div
        className="mp-drop-badge"
        data-mode={dragBadge.mode}
        role="status"
        aria-live="polite"
        // Offset slightly so the badge sits next to (not under) the cursor.
        style={{
          left: dragBadge.x + 14,
          top: dragBadge.y + 14,
        }}
      >
        {dragBadge.mode === 'copy' ? 'Copy' : 'Move'}
      </div>
    )}
    </>
  );
});

